import type { CentralIconBaseProps } from 'central-icons/CentralIconBase';
import type React from 'react';
import { type ButtonHTMLAttributes, forwardRef } from 'react';

import { IconButton, RevelioTooltip } from '@revelio/ui';

interface ActionBarIconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  icon: React.FC<CentralIconBaseProps>;
  iconSize?: number;
  'aria-label'?: string;
  tooltipLabel?: string;
  /** Accepted for compatibility with the previous Chakra-based API. */
  isDisabled?: boolean;
}

export const ActionBarIconButton = forwardRef<
  HTMLButtonElement,
  ActionBarIconButtonProps
>(function ActionBarIconButton(
  {
    icon: IconComponent,
    iconSize = 16,
    'aria-label': ariaLabel,
    tooltipLabel,
    isDisabled,
    disabled,
    ...props
  },
  ref
) {
  const button = (
    <IconButton
      ref={ref}
      variant="ghost"
      size="md"
      shape="squared"
      icon={<IconComponent size={iconSize} />}
      aria-label={(ariaLabel || tooltipLabel) ?? ''}
      disabled={disabled ?? isDisabled}
      {...props}
    />
  );

  if (tooltipLabel) {
    return <RevelioTooltip label={tooltipLabel}>{button}</RevelioTooltip>;
  }

  return button;
});

ActionBarIconButton.displayName = 'ActionBarIconButton';
